export const DEFAULT_CONTENT = {
  site: {
    brandName: "KEVANE",
    brandSub: "Consulting & Training",
    tagline: "Building Stronger Organisations & Communities.",
    footerBlurb:
      "An independent consultancy operated by Paddy Kevane, supporting charities, community and voluntary organisations, non-profits and boards across Ireland.",
    footerNote: "Independent consultancy operated solely by Paddy Kevane.",
    phone: "086 244 7883",
    phoneHref: "+353862447883",
    email: "paddy@kevaneconsulting.ie",
    domain: "www.kevaneconsulting.ie",
    ctaPrimary: "Get in Touch",
    ctaSecondary: "Discuss Your Organisation's Needs",
    nav: { home: "Home", about: "About", services: "Services", training: "Training", contact: "Contact" },
  },
  home: {
    heroOverline: "Kevane Consulting & Training — Paddy Kevane",
    heroLine1: "Building Stronger",
    heroEmphasis: "Organisations",
    heroLine2Suffix: "&",
    heroLine3: "Communities.",
    heroIntro:
      "Independent, practical support in governance, strategy, training and community development — for charities, non-profits, boards, voluntary organisations, social enterprises and other businesses across Ireland.",
    heroImage: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=1600&q=80",
    manifestoOverline: "The Practice",
    manifestoText:
      "A small, independent consultancy with a single point of contact. The person you speak to is the person who does the work — no hand-offs, no jargon, just experienced, hands-on support shaped around your organisation.",
    manifestoLink: "About Paddy Kevane",
    servicesOverline: "What I Do",
    servicesTitle: "Four areas of focus.",
    servicesCta: "Explore Services",
    services: [
      {
        title: "Governance",
        text: "Board effectiveness reviews, governance health-checks and practical support for trustees, chairs and secretaries.",
        image: "https://images.unsplash.com/photo-1515168833906-d2a3b82b302a?auto=format&fit=crop&w=1400&q=80",
      },
      {
        title: "Strategy",
        text: "Strategic planning facilitation and plain-language plans that boards actually use.",
        image: "https://images.unsplash.com/photo-1568992688065-536aad8a12f6?auto=format&fit=crop&w=1400&q=80",
      },
      {
        title: "Training",
        text: "Practical, tailored training for boards, staff and volunteers — from induction to the Charities Governance Code.",
        image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=1400&q=80",
      },
      {
        title: "Community",
        text: "Community consultation, needs analysis, project development and funding support grounded in real local knowledge.",
        image: "https://images.pexels.com/photos/7156162/pexels-photo-7156162.jpeg?auto=compress&cs=tinysrgb&w=1400",
      },
    ],
    aboutOverline: "About",
    aboutTitle1: "One consultant.",
    aboutTitle2: "A wealth of experience.",
    aboutP1:
      "Kevane Consulting & Training is operated solely by Paddy Kevane, a university lecturer with deep experience across the community, voluntary and non-profit sectors. Consultancy work is undertaken part-time, outside lecturing hours — deliberately small in scale, so every client gets direct, personal attention.",
    aboutP2:
      "No associates, no account managers. Just practical, plain-spoken support from someone who has sat on both sides of the boardroom table.",
    aboutImage:
      "https://images.unsplash.com/photo-1510851896000-498520af2236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBkYXJrfGVufDB8fHx8MTc4NjM2MDU0OXww&ixlib=rb-4.1.0&q=85",
    aboutBadge: "Independent by design.",
    aboutCta: "Read the full story",
    banner: {
      overline: "Start the conversation",
      title: "Discuss Your Organisation\u2019s Needs",
      blurb:
        "Every engagement begins with a straightforward chat. No forms, no obligation — just a practical conversation about where your organisation is and where it wants to go.",
    },
  },
  about: {
    heroOverline: "About the Practice",
    heroLines: ["Experience you can", "talk to directly."],
    heroIntro:
      "Kevane Consulting & Training is a small, part-time independent consultancy operated by Paddy Kevane — supporting charities, community and voluntary organisations, non-profits, boards, social enterprises and other businesses across Ireland.",
    logoImage:
      "https://customer-assets-v7afamib.emergentagent.net/job_c8b42a83-dffd-47b7-8cf2-6d9ff909ab8a/artifacts/mc8p1ha1_Logo%20Business.webp",
    logoCaption: "The Kevane Consulting & Training identity",
    chapters: [
      {
        title: "An Independent Practice",
        body: "Kevane Consulting & Training is operated solely by Paddy Kevane. There is no team, no associates and no hand-offs — the person who answers your call is the person who carries out the work. That independence keeps the practice agile, affordable and entirely focused on each client's needs.",
      },
      {
        title: "A University Lecturer",
        body: "Paddy's primary professional role is as a university lecturer. All consultancy and training work is undertaken part-time, outside his lecturing hours. Clients benefit from academic rigour and up-to-date thinking, delivered in a practical, down-to-earth way.",
      },
      {
        title: "A Practical Approach",
        body: "Good advice is only useful if it can be acted on. Every piece of work — a board review, a strategic plan, a training session — is delivered in plain language, shaped around the realities of volunteer boards and small staff teams, and built to be used long after the engagement ends.",
      },
    ],
    principlesOverline: "How I Work",
    principlesTitle1: "Principles before",
    principlesTitle2: "process.",
    principlesText:
      "Working with volunteer-led organisations means respecting their time, their resources and their purpose. These four commitments shape every engagement.",
    principles: [
      "Independence — one consultant, no conflicts of interest",
      "Plain language — no jargon, no hundred-page reports nobody reads",
      "Realism — recommendations that fit your time, budget and people",
      "Respect — for volunteers, for communities and for your mission",
    ],
    ctaTitle: "Curious how this could work for your organisation?",
    ctaCta: "See the Services",
    banner: {
      overline: "Work with Paddy",
      title: "A conversation costs nothing.",
      blurb:
        "If you're unsure whether your organisation needs governance support, a strategic plan or a training session, the easiest way to find out is to ask.",
    },
  },
  services: {
    heroOverline: "Services",
    heroLines: ["Practical support,", "exactly where you", "need it."],
    heroIntro:
      "Four areas of focus, one way of working: direct, plain-spoken and shaped around your organisation. Every engagement is scoped with you before it begins — no off-the-shelf packages.",
    chapters: [
      {
        title: "Governance",
        tagline: "Strong boards, confident decisions.",
        desc: "",
        image: "https://images.unsplash.com/photo-1515168833906-d2a3b82b302a?auto=format&fit=crop&w=1400&q=80",
        alt: "Committee members seated together at a community meeting",
        items: [
          "Governance health-checks and board effectiveness reviews",
          "Charities Governance Code compliance support",
          "Policies, procedures and board documentation",
          "Board development, induction and succession planning",
          "Dedicated support for chairs, secretaries and trustees",
        ],
      },
      {
        title: "Strategy",
        tagline: "Clear direction, realistic plans.",
        desc: "",
        image: "https://images.unsplash.com/photo-1568992688065-536aad8a12f6?auto=format&fit=crop&w=1400&q=80",
        alt: "Two people in conversation over plans at a table",
        items: [
          "Strategic planning facilitation for boards and staff",
          "Organisational reviews and restructuring support",
          "Operational and business planning",
          "Funding and sustainability strategy",
          "Plain-language strategy documents boards actually use",
        ],
      },
      {
        title: "Training",
        tagline: "Practical learning for boards, staff and volunteers.",
        desc: "",
        image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=1400&q=80",
        alt: "A training workshop in session with a projector screen",
        items: [
          "Board induction and governance training",
          "Strategy and planning workshops",
          "Tailored in-house sessions for your organisation",
          "Evening and weekend sessions to suit volunteer boards",
        ],
        linkLabel: "See the Training page",
      },
      {
        title: "Community",
        tagline: "Grounded in community.",
        desc: "",
        image: "https://images.pexels.com/photos/7156162/pexels-photo-7156162.jpeg?auto=compress&cs=tinysrgb&w=1400",
        alt: "Volunteers working together sorting community donations",
        items: [
          "Community consultation and engagement",
          "Needs analysis and research",
          "Project development and evaluation",
          "Funding application support",
          "Facilitation for community groups and networks",
        ],
      },
    ],
    processOverline: "How Engagements Work",
    processTitle: "Simple, from first call to final report.",
    steps: [
      { t: "Talk it through", d: "A free, no-obligation phone call or email exchange about what your organisation needs." },
      { t: "Agree the scope", d: "A short, clear proposal: what will be done, when, and what it will cost. No surprises." },
      { t: "Get to work", d: "Delivery at times that suit volunteer boards — evenings and weekends included — with a practical output at the end." },
    ],
    banner: {
      overline: "Start the conversation",
      title: "Discuss Your Organisation\u2019s Needs",
      blurb:
        "Every engagement begins with a straightforward chat. No forms, no obligation — just a practical conversation about where your organisation is and where it wants to go.",
    },
  },
  training: {
    heroOverline: "Training",
    heroLines: ["Learning that fits", "real boards and", "real people."],
    heroIntro:
      "Practical training for charities, community and voluntary organisations — designed and delivered by Paddy Kevane, a university lecturer, and tailored to the realities of volunteer-led organisations.",
    formatsOverline: "Delivery",
    formatsTitle: "Built around your board's calendar.",
    formats: [
      { t: "Flexible lengths", d: "Two-hour evening sessions, half-days and full days — timed around volunteer boards and working staff." },
      { t: "In-house & tailored", d: "Delivered to your board, staff team or network, and tailored to your organisation's real documents and challenges." },
      { t: "Across Ireland", d: "Sessions delivered at your premises or a local venue, with online delivery available where it suits." },
      { t: "Lecturer-led", d: "Designed and delivered by a practising university lecturer — rigorous content, accessible delivery." },
    ],
    workshopsOverline: "Sample Workshops",
    workshopsTitle: "A menu to start from — not a straitjacket.",
    workshopsText:
      "Every session is adapted to the organisation in the room. These popular workshops can be combined, shortened or extended to suit your needs.",
    workshops: [
      { title: "Understanding Governance & the Charities Governance Code", d: "What good governance looks like in practice, and how to work through the Code step by step without overwhelm." },
      { title: "The Roles & Responsibilities of Board Members", d: "Ideal for new and existing trustees — duties, boundaries between board and staff, and how to contribute with confidence." },
      { title: "Strategic Planning for Community & Voluntary Organisations", d: "A hands-on session that demystifies strategy and leaves your board with a practical framework to build its own plan." },
      { title: "Running Effective Meetings & Making Good Decisions", d: "Agendas, minutes, quorums and chairing — the unglamorous skills that make boards work." },
      { title: "Community Consultation & Engagement", d: "How to ask, listen and turn community voices into credible evidence and workable actions." },
      { title: "Funding Readiness & Sustainability", d: "Getting your governance, plans and paperwork in shape so funders can say yes." },
    ],
    banner: {
      overline: "Book a conversation",
      title: "Planning training for your board or team?",
      blurb: "Call or email with a rough idea of what you need — numbers, topics and timing — and Paddy will suggest a format that fits.",
    },
  },
  contact: {
    heroOverline: "Contact",
    heroLines: ["Let's talk about", "your organisation."],
    heroIntro: "No contact forms, no booking systems — just a direct line to Paddy. Every enquiry is answered personally.",
    phoneTitle: "Telephone",
    phoneNote: "Tap to call. If Paddy is lecturing, leave a message and he'll call you back.",
    emailTitle: "Email",
    emailNote: "Often the fastest way to reach Paddy. Outline what you need and he'll reply personally.",
    notes: [
      { t: "A part-time practice", d: "Consultancy is undertaken outside Paddy's university lecturing hours — email is often quickest, and he aims to respond within two working days." },
      { t: "Based in Ireland", d: "Working with charities, non-profits, boards, community organisations, social enterprises and other businesses nationwide, in person or online." },
      { t: "kevaneconsulting.ie", d: "This website will soon live at www.kevaneconsulting.ie — same name, same person, same practical support." },
    ],
    quoteOverline: "Paddy Kevane",
    quote: "The best engagements start with an honest conversation.",
    quoteText:
      "Whether it's a governance question that keeps coming back, a strategy that needs writing, or a board that needs training — get in touch and we'll work out the right next step together.",
  },
};
